<template>
  <div class="major-ranking-page">
    <div v-loading="loading" class="ranking-container">
      <!-- 返回按钮 -->
      <div class="header-section">
        <el-button 
          type="text" 
          icon="el-icon-arrow-left" 
          @click="goBack"
          class="back-btn"
        >
          返回
        </el-button>
      </div>

      <!-- 专业信息和筛选区域 -->
      <div v-if="majorInfo" class="major-info-section">
        <div class="info-card">
          <div class="info-header">
            <h1 class="major-title">
              {{ majorInfo.name }}
              <span v-if="majorInfo.code" class="major-code">
                {{ majorInfo.code }}
              </span>
            </h1>
            <div class="major-stats">
              <el-tag type="primary" size="medium">
                共 {{ rankingList.length }} 所大学开设
              </el-tag>
            </div>
          </div>
          
          <div v-if="majorInfo.description" class="major-description">
            <h3><i class="el-icon-document"></i> 专业简介</h3>
            <p>{{ majorInfo.description }}</p>
          </div>
          
          <!-- 筛选和搜索区域 -->
          <div class="filter-section-inner">
            <h3><i class="el-icon-search"></i> 筛选条件</h3>
            <div class="filter-row">
              <div class="filter-item">
                <label>地区筛选：</label>
                <el-select 
                  v-model="selectedRegion" 
                  placeholder="选择地区" 
                  clearable
                  @change="handleFilterChange"
                  style="width: 150px;"
                >
                  <el-option
                    v-for="region in regionOptions"
                    :key="region"
                    :label="region"
                    :value="region"
                  />
                </el-select>
              </div>
              
              <div class="filter-item">
                <label>大学类型：</label>
                <el-select 
                  v-model="selectedType" 
                  placeholder="选择类型" 
                  clearable
                  @change="handleFilterChange"
                  style="width: 150px;"
                >
                  <el-option
                    v-for="type in typeOptions"
                    :key="type"
                    :label="type"
                    :value="type"
                  />
                </el-select>
              </div>
              
              <div class="filter-item">
                <label>等级筛选：</label>
                <el-select 
                  v-model="selectedGrade" 
                  placeholder="选择等级" 
                  clearable
                  @change="handleFilterChange"
                  style="width: 120px;"
                >
                  <el-option label="A+" value="A+" />
                  <el-option label="A" value="A" />
                  <el-option label="A-" value="A-" />
                  <el-option label="B+" value="B+" />
                  <el-option label="B" value="B" />
                  <el-option label="B-" value="B-" />
                  <el-option label="C+" value="C+" />
                  <el-option label="C" value="C" />
                  <el-option label="C-" value="C-" />
                </el-select>
              </div>
              
              <div class="filter-item">
                <label>标签筛选：</label>
                <el-select 
                  v-model="selectedTag" 
                  placeholder="选择标签" 
                  clearable
                  @change="handleFilterChange"
                  style="width: 120px;"
                >
                  <el-option label="985" value="985" />
                  <el-option label="211" value="211" />
                  <el-option label="双一流" value="双一流" />
                  <el-option label="一流" value="一流" />
                </el-select>
              </div>
              
              <div class="filter-item">
                <label>搜索大学：</label>
                <el-input
                  v-model="searchKeyword"
                  placeholder="输入大学名称"
                  @input="handleFilterChange"
                  style="width: 200px;"
                  clearable
                >
                  <i slot="prefix" class="el-input__icon el-icon-search"></i>
                </el-input>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 排行榜表格 -->
      <div v-if="filteredRankingList && filteredRankingList.length > 0" class="ranking-table-section">
        <div class="table-card">
          <div class="table-header">
            <h2 class="section-title">
              <i class="el-icon-trophy"></i>
              {{ majorInfo ? majorInfo.name : '专业' }}排行榜
            </h2>
            <div class="table-stats">
              <span>共 {{ filteredRankingList.length }} 条结果</span>
            </div>
          </div>
          
          <div class="table-wrapper">
            <el-table
              :data="paginatedRankingList"
              stripe
              border
              style="width: 100%"
              :default-sort="{ prop: 'rank', order: 'ascending' }"
              @row-click="handleUniversityClick"
              class="ranking-table"
            >
              <el-table-column
                type="index"
                label="序号"
                width="80"
                align="center"
                :index="getTableIndex"
              />
              
              <el-table-column
                prop="rank"
                label="排名"
                width="100"
                align="center"
                sortable
              >
                <template slot-scope="scope">
                  <div class="rank-cell">
                    <span 
                      class="rank-number"
                      :class="getRankClass(scope.row.rank)"
                    >
                      {{ scope.row.rank || '-' }}
                    </span>
                  </div>
                </template>
              </el-table-column>
              
              <el-table-column
                prop="university_name"
                label="大学名称"
                min-width="200"
                show-overflow-tooltip
              >
                <template slot-scope="scope">
                  <div class="university-cell">
                    <div class="university-name">{{ scope.row.university_name }}</div>
                    <div v-if="scope.row.english_name" class="english-name">
                      {{ scope.row.english_name }}
                    </div>
                  </div>
                </template>
              </el-table-column>
              
              <el-table-column
                prop="area"
                label="地区"
                width="120"
                align="center"
              />
              
              <el-table-column
                prop="type"
                label="大学类型"
                width="140"
                align="center"
              />
              
              <el-table-column
                prop="grade"
                label="等级"
                width="100"
                align="center"
                sortable
              >
                <template slot-scope="scope">
                  <el-tag 
                    :type="getGradeTagType(scope.row.grade)"
                    size="small"
                    v-if="scope.row.grade"
                  >
                    {{ scope.row.grade }}
                  </el-tag>
                  <span v-else>-</span>
                </template>
              </el-table-column>
              
              <el-table-column
                prop="tag"
                label="大学标签"
                width="160"
                align="center"
              >
                <template slot-scope="scope">
                  <div class="tag-container" v-if="scope.row.tag">
                    <el-tag 
                      v-for="(tag, index) in parseTags(scope.row.tag)" 
                      :key="index"
                      size="mini"
                      :type="getTagType(tag)"
                      class="university-tag"
                    >
                      {{ tag }}
                    </el-tag>
                  </div>
                  <span v-else>-</span>
                </template>
              </el-table-column>
              
              <el-table-column
                prop="ranking_type"
                label="排名类型"
                width="120"
                align="center"
              />
              
              <el-table-column
                label="操作"
                width="120"
                align="center"
                fixed="right"
              >
                <template slot-scope="scope">
                  <el-button
                    type="text"
                    size="small"
                    @click.stop="handleUniversityClick(scope.row)"
                    class="action-btn"
                  >
                    查看详情
                  </el-button>
                </template>
              </el-table-column>
            </el-table>
          </div>
          
          <!-- 分页 -->
          <div class="pagination-wrapper">
            <el-pagination
              @size-change="handleSizeChange"
              @current-change="handleCurrentChange"
              :current-page="currentPage"
              :page-sizes="[10, 20, 50, 100]"
              :page-size="pageSize"
              layout="total, sizes, prev, pager, next, jumper"
              :total="filteredRankingList.length"
            />
          </div>
        </div>
      </div>

      <!-- 空状态 -->
      <div v-else-if="!loading" class="empty-state">
        <i class="el-icon-info"></i>
        <p v-if="searchKeyword || selectedRegion || selectedType || selectedGrade || selectedTag">
          未找到符合筛选条件的大学
        </p>
        <p v-else>暂无该专业的排行榜信息</p>
        <el-button 
          v-if="searchKeyword || selectedRegion || selectedType || selectedGrade || selectedTag"
          type="primary" 
          size="small" 
          @click="clearFilters"
        >
          清除筛选条件
        </el-button>
      </div>
    </div>
    
    <!-- 回到顶部按钮 -->
    <el-backtop 
      :visibility-height="300"
      :bottom="50"
      :right="50"
    >
      <div class="back-to-top">
        <i class="el-icon-caret-top"></i>
      </div>
    </el-backtop>
  </div>
</template>

<script>
import request from '@/http/request'

export default {
  name: 'MajorRanking',
  data() {
    return {
      loading: false,
      majorInfo: null,
      rankingList: [],
      filteredRankingList: [],
      
      // 筛选条件
      selectedRegion: '',
      selectedType: '',
      selectedGrade: '',
      selectedTag: '',
      searchKeyword: '',
      
      // 筛选选项
      regionOptions: [],
      typeOptions: [],
      
      // 分页
      currentPage: 1,
      pageSize: 10
    }
  },
  computed: {
    paginatedRankingList() {
      const start = (this.currentPage - 1) * this.pageSize
      const end = start + this.pageSize
      return this.filteredRankingList.slice(start, end)
    }
  },
  created() {
    this.fetchMajorRanking()
  },
  watch: {
    '$route'(to, from) {
      // 路由变化时重新获取数据
      if (to.query.id !== from.query.id || to.query.name !== from.query.name) {
        this.fetchMajorRanking()
      }
    }
  },
  methods: {
    async fetchMajorRanking() {
      const majorId = this.$route.query.id
      const majorName = this.$route.query.name
      const majorCode = this.$route.query.code
      
      console.log('路由参数:', { majorId, majorName, majorCode })
      
      // 验证专业ID是否有效
      const parsedMajorId = parseInt(majorId)
      console.log('解析后的专业ID:', parsedMajorId)
      
      if ((!majorId || isNaN(parsedMajorId)) && !majorName) {
        this.$message.warning('专业信息不完整，缺少有效的专业ID')
        console.error('专业信息验证失败:', { majorId, parsedMajorId, majorName })
        this.goBack()
        return
      }

      // 如果没有有效的专业ID，尝试使用模拟数据
      if (!majorId || isNaN(parsedMajorId)) {
        this.$message.info(`专业ID无效(${majorId})，使用演示数据展示 ${majorName}`)
        this.loadMockData()
        return
      }

      this.loading = true
      try {
        // 使用真实的API接口获取开设该专业的大学列表
        console.log('调用API:', '/api/Subspecialty/getUniversityBySub', { sub_id: parsedMajorId })
        const result = await request('/api/Subspecialty/getUniversityBySub', { sub_id: parsedMajorId }, 'get')
        
        console.log('API返回结果:', result)
        
        if (result && Array.isArray(result) && result.length > 0) {
          // 设置专业信息
          this.majorInfo = { 
            name: majorName || '未知专业',
            code: majorCode || '',
            description: `${majorName || '该专业'}是一门重要的学科专业，为社会培养专业人才。`
          }
          
          // 转换API数据格式为排行榜格式
          this.rankingList = result.map((university, index) => ({
            id: university.id,
            university_name: university.name,
            english_name: university.english_name,
            area: university.area,
            type: university.type,
            grade: this.generateGradeFromRank(university.rank), // 根据排名生成等级
            rank: university.rank || (index + 1),
            ranking_type: '综合排名',
            tag: university.tag,
            is_settled: university.is_settled
          }))
          
          console.log('转换后的排行榜数据:', this.rankingList)
          
          // 初始化筛选数据
          this.initFilterOptions()
          this.applyFilters()
        } else {
          console.warn('API返回空数据或格式不正确:', result)
          this.$message.warning('该专业暂无大学排行榜数据，使用演示数据')
          // 如果API返回空数据，使用模拟数据进行演示
          this.loadMockData()
        }
      } catch (error) {
        console.error('获取专业排行榜失败:', error)
        this.$message.error(`获取数据失败: ${error.message || '网络错误'}，使用演示数据`)
        
        // 如果API调用失败，使用模拟数据进行演示
        this.loadMockData()
      } finally {
        this.loading = false
      }
    },
    
    // 加载模拟数据（用于演示）
    loadMockData() {
      const majorName = this.$route.query.name || '计算机科学与技术'
      const majorCode = this.$route.query.code || '080901'
      
      console.log('加载模拟数据:', { majorName, majorCode })
      
      this.majorInfo = {
        name: majorName,
        code: majorCode,
        description: `${majorName}是一门重要的学科专业，为社会培养具有专业素养和实践能力的高级专门人才。该专业注重理论与实践相结合，培养学生的创新思维和解决实际问题的能力。`
      }
      
      // 生成模拟排行榜数据
      const universities = [
        { name: '清华大学', english_name: 'Tsinghua University', area: '北京', type: '综合类', grade: 'A+', rank: 1 },
        { name: '北京大学', english_name: 'Peking University', area: '北京', type: '综合类', grade: 'A+', rank: 2 },
        { name: '浙江大学', english_name: 'Zhejiang University', area: '浙江', type: '综合类', grade: 'A+', rank: 3 },
        { name: '国防科技大学', english_name: 'National University of Defense Technology', area: '湖南', type: '军事类', grade: 'A+', rank: 4 },
        { name: '北京航空航天大学', english_name: 'Beihang University', area: '北京', type: '理工类', grade: 'A', rank: 5 },
        { name: '北京邮电大学', english_name: 'Beijing University of Posts and Telecommunications', area: '北京', type: '理工类', grade: 'A', rank: 6 },
        { name: '哈尔滨工业大学', english_name: 'Harbin Institute of Technology', area: '黑龙江', type: '理工类', grade: 'A', rank: 7 },
        { name: '上海交通大学', english_name: 'Shanghai Jiao Tong University', area: '上海', type: '综合类', grade: 'A', rank: 8 },
        { name: '南京大学', english_name: 'Nanjing University', area: '江苏', type: '综合类', grade: 'A', rank: 9 },
        { name: '华中科技大学', english_name: 'Huazhong University of Science and Technology', area: '湖北', type: '理工类', grade: 'A', rank: 10 },
        { name: '电子科技大学', english_name: 'University of Electronic Science and Technology of China', area: '四川', type: '理工类', grade: 'A', rank: 11 },
        { name: '北京理工大学', english_name: 'Beijing Institute of Technology', area: '北京', type: '理工类', grade: 'A-', rank: 12 },
        { name: '东北大学', english_name: 'Northeastern University', area: '辽宁', type: '理工类', grade: 'A-', rank: 13 },
        { name: '吉林大学', english_name: 'Jilin University', area: '吉林', type: '综合类', grade: 'A-', rank: 14 },
        { name: '同济大学', english_name: 'Tongji University', area: '上海', type: '理工类', grade: 'A-', rank: 15 },
        { name: '中国科学技术大学', english_name: 'University of Science and Technology of China', area: '安徽', type: '理工类', grade: 'A-', rank: 16 },
        { name: '西北工业大学', english_name: 'Northwestern Polytechnical University', area: '陕西', type: '理工类', grade: 'A-', rank: 17 },
        { name: '西安交通大学', english_name: 'Xi\'an Jiaotong University', area: '陕西', type: '综合类', grade: 'A-', rank: 18 },
        { name: '西安电子科技大学', english_name: 'Xidian University', area: '陕西', type: '理工类', grade: 'A-', rank: 19 },
        { name: '山东大学', english_name: 'Shandong University', area: '山东', type: '综合类', grade: 'B+', rank: 20 }
      ]
      
      this.rankingList = universities.map((uni, index) => ({
        id: index + 1,
        university_name: uni.name,
        english_name: uni.english_name,
        area: uni.area,
        type: uni.type,
        grade: uni.grade,
        rank: uni.rank,
        ranking_type: '教育部学科评估'
      }))
      
      this.initFilterOptions()
      this.applyFilters()
    },
    
    generateGradeFromRank(rank) {
      // 根据排名生成等级评定
      if (!rank) return ''
      if (rank <= 5) return 'A+'
      if (rank <= 15) return 'A'
      if (rank <= 30) return 'A-'
      if (rank <= 50) return 'B+'
      if (rank <= 80) return 'B'
      if (rank <= 120) return 'B-'
      if (rank <= 160) return 'C+'
      if (rank <= 200) return 'C'
      return 'C-'
    },
    
    initFilterOptions() {
      // 提取地区选项
      this.regionOptions = [...new Set(this.rankingList.map(item => item.area).filter(Boolean))]
      
      // 提取类型选项
      this.typeOptions = [...new Set(this.rankingList.map(item => item.type).filter(Boolean))]
    },
    
    applyFilters() {
      let filtered = [...this.rankingList]
      
      // 地区筛选
      if (this.selectedRegion) {
        filtered = filtered.filter(item => item.area === this.selectedRegion)
      }
      
      // 类型筛选
      if (this.selectedType) {
        filtered = filtered.filter(item => item.type === this.selectedType)
      }
      
      // 等级筛选
      if (this.selectedGrade) {
        filtered = filtered.filter(item => item.grade === this.selectedGrade)
      }
      
      // 标签筛选
      if (this.selectedTag) {
        filtered = filtered.filter(item => 
          item.tag && item.tag.includes(this.selectedTag)
        )
      }
      
      // 搜索筛选
      if (this.searchKeyword) {
        const keyword = this.searchKeyword.toLowerCase()
        filtered = filtered.filter(item => 
          item.university_name.toLowerCase().includes(keyword) ||
          (item.english_name && item.english_name.toLowerCase().includes(keyword))
        )
      }
      
      this.filteredRankingList = filtered
      this.currentPage = 1 // 重置到第一页
    },
    
    handleFilterChange() {
      this.applyFilters()
    },
    
    clearFilters() {
      this.selectedRegion = ''
      this.selectedType = ''
      this.selectedGrade = ''
      this.selectedTag = ''
      this.searchKeyword = ''
      this.applyFilters()
    },
    
    handleSizeChange(val) {
      this.pageSize = val
      this.currentPage = 1
    },
    
    handleCurrentChange(val) {
      this.currentPage = val
    },
    
    getTableIndex(index) {
      return (this.currentPage - 1) * this.pageSize + index + 1
    },
    
    getRankClass(rank) {
      if (rank <= 3) return 'top-three'
      if (rank <= 10) return 'top-ten'
      return ''
    },
    
    getGradeTagType(grade) {
      if (!grade) return 'info'
      if (grade.includes('A+')) return 'danger'
      if (grade.includes('A')) return 'warning'
      if (grade.includes('B+')) return 'success'
      if (grade.includes('B')) return 'info'
      return 'info'
    },
    
    parseTags(tagString) {
      if (!tagString) return []
      return tagString.split(',').map(tag => tag.trim()).filter(tag => tag)
    },
    
    getTagType(tag) {
      if (tag.includes('985')) return 'danger'
      if (tag.includes('211')) return 'warning'
      if (tag.includes('双一流')) return 'success'
      if (tag.includes('一流')) return 'primary'
      return 'info'
    },
    
    handleUniversityClick(row) {
      // 跳转到大学详情页面
      this.$router.push({
        path: '/radar/university',
        query: {
          id: row.id,
          name: row.university_name || row.name
        }
      })
    },
    
    goBack() {
      this.$router.go(-1)
    }
  }
}
</script>

<style lang="scss" scoped>
.major-ranking-page {
  width: 100%;
  height: 100vh;
  background: #f5f7fa;
  padding: 20px;
  overflow-y: auto;
  overflow-x: hidden;
  box-sizing: border-box;
}

.ranking-container {
  max-width: 1400px;
  margin: 0 auto;
  width: 100%;
  /* 确保容器内容总是超过视口高度，触发滚动 */
  min-height: calc(100vh + 100px);
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.header-section {
  .back-btn {
    font-size: 16px;
    color: #606266;
    padding: 0;
    
    &:hover {
      color: #409eff;
    }
  }
}

.major-info-section {
  /* margin已通过容器的gap处理 */
  position: relative;
}

.info-card {
  background: #ffffff;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
}

.info-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 20px;
  padding-bottom: 20px;
  border-bottom: 2px solid #f0f2f5;
}

.major-title {
  margin: 0;
  font-size: 28px;
  font-weight: 600;
  color: #1e293b;
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.major-code {
  font-size: 16px;
  color: #64748b;
  font-weight: normal;
  background: #f1f5f9;
  padding: 4px 8px;
  border-radius: 4px;
}

.major-stats {
  margin-left: 12px;
}

.major-description {
  margin-bottom: 24px;
  
  h3 {
    font-size: 16px;
    font-weight: 600;
    color: #374151;
    margin: 0 0 12px 0;
    display: flex;
    align-items: center;
    gap: 8px;
    
    i {
      color: #409eff;
    }
  }
  
  p {
    font-size: 14px;
    color: #6b7280;
    line-height: 1.6;
    margin: 0;
  }
}

.filter-section-inner {
  border-top: 1px solid #e5e7eb;
  padding-top: 20px;
  
  h3 {
    font-size: 16px;
    font-weight: 600;
    color: #374151;
    margin: 0 0 16px 0;
    display: flex;
    align-items: center;
    gap: 8px;
    
    i {
      color: #409eff;
    }
  }
}


.filter-row {
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  align-items: center;
}

.filter-item {
  display: flex;
  align-items: center;
  gap: 8px;
  
  label {
    font-size: 14px;
    color: #374151;
    font-weight: 500;
    white-space: nowrap;
  }
}

.ranking-table-section {
  /* margin已通过容器的gap处理 */
  position: relative;
}

.table-card {
  background: #ffffff;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
}

.table-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 16px;
  border-bottom: 2px solid #f0f2f5;
}

.section-title {
  font-size: 20px;
  font-weight: 600;
  color: #1e293b;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  
  i {
    color: #f39c12;
    font-size: 22px;
  }
}

.table-stats {
  font-size: 14px;
  color: #64748b;
}

.table-wrapper {
  /* 移除固定高度限制，让表格自然展开 */
  overflow-x: auto; /* 只在水平方向添加滚动 */
  
  &::-webkit-scrollbar {
    width: 8px;
    height: 8px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: #94a3b8;
  }
  
  &::-webkit-scrollbar-track {
    background: #f1f5f9;
    border-radius: 4px;
  }
}

.ranking-table {
  ::v-deep .el-table__row {
    cursor: pointer;
    transition: all 0.3s;
    
    &:hover {
      background-color: #f8fafc !important;
    }
  }
}

.rank-cell {
  display: flex;
  justify-content: center;
  align-items: center;
}

.rank-number {
  font-weight: 600;
  font-size: 16px;
  
  &.top-three {
    color: #f56565;
    font-size: 18px;
    font-weight: 700;
  }
  
  &.top-ten {
    color: #ed8936;
    font-weight: 700;
  }
}

.university-cell {
  .university-name {
    font-weight: 600;
    color: #1e293b;
    font-size: 15px;
  }
  
  .english-name {
    font-size: 12px;
    color: #64748b;
    font-style: italic;
    margin-top: 2px;
  }
}

.action-btn {
  color: #409eff;
  font-weight: 500;
  
  &:hover {
    color: #66b1ff;
  }
}

.tag-container {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  justify-content: center;
  align-items: center;
}

.university-tag {
  margin: 0;
  font-size: 11px;
}

.pagination-wrapper {
  margin-top: 24px;
  display: flex;
  justify-content: center;
}

.empty-state {
  text-align: center;
  padding: 80px 20px;
  background: #ffffff;
  border-radius: 12px;
  color: #94a3b8;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  
  i {
    font-size: 64px;
    margin-bottom: 20px;
    display: block;
    color: #cbd5e1;
  }
  
  p {
    font-size: 16px;
    margin: 0 0 20px 0;
  }
}

// 全局滚动条样式
.major-ranking-page {
  &::-webkit-scrollbar {
    width: 8px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 4px;
    
    &:hover {
      background: #94a3b8;
    }
  }
  
  &::-webkit-scrollbar-track {
    background: #f1f5f9;
    border-radius: 4px;
  }
}

// 确保各个部分有适当的间距和布局
.header-section {
  flex-shrink: 0; /* 防止压缩 */
}

.major-info-section {
  flex-shrink: 0;
}

.filter-section {
  flex-shrink: 0;
}

.ranking-table-section {
  flex-shrink: 0; /* 让表格自然展开，不压缩 */
  /* 移除flex: 1，让内容自然撑开页面高度 */
}

.empty-state {
  min-height: 400px; /* 确保空状态也有足够高度 */
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}

// 响应式设计
@media (max-width: 1200px) {
  .ranking-container {
    max-width: 100%;
  }
}

@media (max-width: 768px) {
  .major-ranking-page {
    padding: 10px;
    /* 确保移动端也有正确的滚动 */
    height: 100vh;
  }
  
  .ranking-container {
    gap: 15px; /* 移动端减少间距 */
  }
  
  .filter-row {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
  }
  
  .filter-item {
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
    
    label {
      font-size: 13px;
    }
  }
  
  .major-title {
    font-size: 22px;
    flex-direction: column;
    align-items: flex-start;
  }
  
  .info-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }
  
  .table-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }
  
  /* 移动端表格优化 */
  .table-wrapper {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch; /* iOS平滑滚动 */
  }
}

// 回到顶部按钮样式
.back-to-top {
  width: 40px;
  height: 40px;
  background: #409eff;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 18px;
  transition: all 0.3s;
  box-shadow: 0 2px 12px rgba(64, 158, 255, 0.3);
  
  &:hover {
    background: #66b1ff;
    transform: translateY(-2px);
    box-shadow: 0 4px 20px rgba(64, 158, 255, 0.4);
  }
}
</style>
